//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { createContext, type RefObject } from 'react';

export interface TourState {
  isNextDisabled: boolean;
  activeTour: string | null;
  tourStep: number;
  // Tours the user asked never to start on their own again. Kept in this
  // browser rather than on the profile, so a private window sees them anew.
  optedOutTours: string[];
}

interface HelpState {
  isPulsing: boolean;
  helpInitialized: boolean;
  timerActive: boolean;
}

export interface TourActions {
  setIsNextDisabled: (disabled: boolean) => void;
  startTour: (tourId: string) => void;
  endTour: () => void;
  setTourStep: (step: number | ((prev: number) => number)) => void;
  // Ends the tour and records that the user is done with it, so it does not
  // start itself again. Takes no id: the provider knows which tour is
  // running, and a caller that named one could name the wrong one.
  finishTour: () => void;
}

export interface StartTimerOptions {
  timerSeconds?: number;
  label?: string;
}

// Asked when the inactivity timer fires, before the Help icon pulses. Returns
// true when it took the moment -- started a tour, say -- and the icon then
// stays quiet.
export type HelpMomentHandler = () => boolean;

interface HelpActions {
  showHelpIndicators: () => void;
  hideHelpIndicators: () => void;
  startInactivityTimer: (options?: StartTimerOptions) => boolean;
  cancelInactivityTimer: () => boolean;
  setHelpInitialized: (initialized: boolean) => void;
  // At most one handler; null withdraws it.
  setHelpMomentHandler: (handler: HelpMomentHandler | null) => void;
}

// Where the keyboard can go when a tour ends, for the rungs the tour
// itself cannot see. Refs rather than state: they are read once, on the way
// out, and a change to either should never re-render a consumer.
export interface TourFocus {
  // What had the keyboard when the tour began, recorded before the first
  // step could take it away. A tour navigates, so this is often detached by
  // the end, which is what the rung below it is for.
  readonly originRef: RefObject<HTMLElement | null>;
  // The Help button, which outlives the menu it opens, every route the tour
  // visits, and the tour itself. Registered by the button's own component.
  readonly fallbackRef: RefObject<HTMLElement | null>;
}

export interface HelpContextValue {
  tourState: TourState;
  helpState: HelpState;
  tourActions: TourActions;
  helpActions: HelpActions;
  tourFocus: TourFocus;
}

// Context for help and tour state management.
const HelpContext = createContext<HelpContextValue | undefined>(undefined);

export { HelpContext };
